import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { MasterPackage, PackagingBottomSheetProps } from '@helpers/Models';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { useToast } from '@contexts/Toast';
import { errorMessage, OrderCreationMasterData } from '@helpers/Constants';
import { searchService } from '@services/OrderCreationService';

export const PackagingBottomSheet: React.FC<PackagingBottomSheetProps> = ({
  onClose,
  onSelect,
  packageIndex,
}) => {
  const [searchKey, setSearchKey] = useState('');
  const [masterPackages, setMasterPackages] = useState<MasterPackage[]>([]);
  const { showToastMessage } = useToast();
  const [filteredPackages, setFilteredPackages] = useState<MasterPackage[]>([]);
  const canAccessPaidPackaging = (() => {
    try {
      const modules = JSON.parse(localStorage.getItem('modules') ?? '[]');
      return Array.isArray(modules) && modules.includes(5);
    } catch {
      return false;
    }
  })();

  /**
   * Runs once on component mount to fetch master packaging data.
   * Calls `fetchMasterPackages` to load packaging packs from the server.
   */
  useEffect(() => {
    fetchMasterPackages();
  }, []);

  /**
   * useEffect hook to filter master packages based on a search key.
   *
   * This effect runs whenever either `searchKey` or `masterPackages` changes.
   * It uses the `searchService` utility to filter `masterPackages` by the
   * `packageType` field and stores the filtered result in the `filteredPackages` state.
   *
   * Dependencies:
   * - `searchKey`: The keyword to filter the packages by.
   * - `masterPackages`: The original unfiltered list of all package data.
   */
  useEffect(() => {
    const result = searchService(masterPackages, searchKey, 'packageType');
    setFilteredPackages(result);
  }, [searchKey, masterPackages]);

  /**
   * Fetches master package data from the API.
   * Sets the master packages state with the received data.
   * Shows an error toast if the fetch fails.
   */
  const fetchMasterPackages = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      const masterData = response.data.data.masterData.packageType;
      setMasterPackages(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Handles the selection of a packaging type and triggers the appropriate callbacks.
   *
   * @param {number} packId - The ID of the selected product packaging type.
   * @param {number} packageIndex - The context or purpose for which the package is selected.
   *
   * Calls `onSelect` with the selected packaging data, then calls `onClose` to close the UI.
   */
  const handlePackagingSelect = (packId: number, packageIndex: number) => {
    const selectedPackage = masterPackages.find((item) => item.id === packId);
    const isPaidPackage = Number(selectedPackage?.price ?? 0) > 0;

    if (!canAccessPaidPackaging && isPaidPackage) {
      return;
    }

    onSelect({ productPackageTypeId: packId, packageIndex: packageIndex });
    onClose();
  };

  return (
    <div className="fixed z-10 inset-0 bg-black bg-opacity-40 flex justify-center items-end w-full h-full">
      <div className="bg-white rounded-t-2xl w-full p-6 shadow-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-semibold text-lg">Add Packaging</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                value={searchKey}
                placeholder="Search..."
                onChange={(e) => setSearchKey(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 h-8 w-72 text-sm"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-600 hover:text-gray-900 text-xl"
            >
              ✕
            </button>
          </div>
        </div>
        {/* Table Header */}
        <div className="grid grid-cols-3 items-center text-sm font-semibold text-gray-700 border-b pb-2 mb-2 bg-gray-200">
          <span>Title</span>
          <span className="text-center">Size in lbs</span>
          <span className="text-right">Upcharge</span>
        </div>
        {/* Table Rows */}
        {filteredPackages.map((pack) => (
          <button
            type="button"
            key={pack.id}
            disabled={!canAccessPaidPackaging && Number(pack.price ?? 0) > 0}
            onClick={() => handlePackagingSelect(pack.id, packageIndex ?? 0)}
            className={`grid grid-cols-3 w-full text-sm py-2 text-left ${!canAccessPaidPackaging && Number(pack.price ?? 0) > 0
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-tertiary hover:border hover:border-primary'
              }`}
          >
            <span>{pack.packageType}</span>
            <span className="text-center">{pack.packageSize}</span>
            <span className="text-right text-gray-800">
              + ${pack.price.toFixed(2)} /{pack.unit}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
